// apps/web/src/components/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from './Toast';
import TemplateBuilder from './TemplateBuilder';
import AuditDashboard from './AuditDashboard';
import SubscriptionManagement from './SubscriptionManagement';

/**
 * AdminDashboard - Template marketplace administration
 * - Template management (list, view, delete, publish/unpublish)
 * - Sales analytics
 * - Future: WYSIWYG template builder
 *
 * Access: Admin users only (role === 'admin')
 */
export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('templates'); // 'templates' | 'analytics' | 'create' | 'help'
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [timePeriod, setTimePeriod] = useState('all'); // 'all' | '30d' | '90d' | '365d'

  // Auth check - redirect if not admin
  useEffect(() => {
    console.log('[AdminDashboard] Auth check - user:', user);
    if (!user) {
      console.log('[AdminDashboard] No user - redirecting to home');
      toast.error('Please log in to access admin dashboard');
      navigate('/');
      return;
    }

    console.log('[AdminDashboard] User role:', user.role);
    // Allow both 'admin' and 'owner' roles
    if (user.role !== 'admin' && user.role !== 'owner') {
      console.log('[AdminDashboard] Access denied - role is:', user.role);
      toast.error('Access denied: Admin privileges required');
      navigate('/');
      return;
    }
    console.log('[AdminDashboard] Access granted for role:', user.role);
  }, [user, navigate, toast]);

  // Load data on mount and tab change
  useEffect(() => {
    // Allow both 'admin' and 'owner' roles
    if (!user || (user.role !== 'admin' && user.role !== 'owner')) return;

    if (activeTab === 'templates') {
      loadTemplates();
    } else if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab, user, timePeriod]); // Reload when time period changes

  async function loadTemplates() {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/admin/templates`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (err) {
      toast.error('Failed to load templates: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadAnalytics() {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/admin/analytics?period=${timePeriod}`;
      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setAnalytics(data);
    } catch (err) {
      toast.error('Failed to load analytics: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function togglePublished(templateId, currentStatus) {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/admin/templates/${encodeURIComponent(templateId)}/publish`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ is_published: !currentStatus }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      toast.success(`Template ${!currentStatus ? 'published' : 'unpublished'} successfully`);
      loadTemplates();
    } catch (err) {
      toast.error('Failed to update template: ' + err.message);
    }
  }

  async function deleteTemplate(templateId) {
    if (!window.confirm('Are you sure you want to delete this template? This cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/admin/templates/${encodeURIComponent(templateId)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      toast.success('Template deleted successfully');
      loadTemplates();
    } catch (err) {
      toast.error('Failed to delete template: ' + err.message);
    }
  }

  function handleEditTemplate(template) {
    setEditingTemplate(template);
    setActiveTab('create');
  }

  async function handleResetAnalytics() {
    if (!window.confirm('⚠️ WARNING: This will permanently delete ALL purchase records and reset all analytics counters to 0. This action cannot be undone.\n\nAre you absolutely sure you want to proceed?')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/admin/analytics/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ confirm: 'RESET' }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      toast.success(`Analytics reset: ${data.purchases_deleted} purchases deleted, counters reset to 0`);

      // Reload analytics
      if (activeTab === 'analytics') {
        loadAnalytics();
      }
      // Reload templates to show updated counts
      loadTemplates();
    } catch (err) {
      toast.error('Failed to reset analytics: ' + err.message);
    }
  }

  // Don't render anything if not authenticated admin or owner
  console.log('[AdminDashboard] Render check - user:', user, 'role:', user?.role);
  if (!user || (user.role !== 'admin' && user.role !== 'owner')) {
    console.log('[AdminDashboard] Render blocked - returning null');
    return null;
  }
  console.log('[AdminDashboard] Rendering admin panel');

  return (
    <div className="AdminDashboard">
      {/* Header */}
      <div className="AdminHeader">
        <div>
          <h1 className="AdminTitle">Template Marketplace Admin</h1>
          <p className="AdminSubtitle">Manage templates, view analytics, and more</p>
        </div>
        <button className="Btn" onClick={() => navigate('/')}>
          ← Back to App
        </button>
      </div>

      {/* Tabs */}
      <div className="AdminTabs">
        <button
          className={`AdminTab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          📋 Templates
        </button>
        <button
          className={`AdminTab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📊 Analytics
        </button>
        <button
          className={`AdminTab ${activeTab === 'subscriptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          💳 Subscriptions
        </button>
        <button
          className={`AdminTab ${activeTab === 'create' ? 'active' : ''}`}
          onClick={() => setActiveTab('create')}
        >
          ✏️ Create Template
        </button>
        <button
          className={`AdminTab ${activeTab === 'audit' ? 'active' : ''}`}
          onClick={() => setActiveTab('audit')}
        >
          🔍 Audit Logs
        </button>
        <button
          className={`AdminTab ${activeTab === 'help' ? 'active' : ''}`}
          onClick={() => setActiveTab('help')}
        >
          ❓ Help
        </button>
      </div>

      {/* Content */}
      <div className="AdminContent">
        {loading ? (
          <div className="AdminLoading">Loading...</div>
        ) : activeTab === 'templates' ? (
          <TemplatesView
            templates={templates}
            onTogglePublished={togglePublished}
            onEdit={handleEditTemplate}
            onDelete={deleteTemplate}
          />
        ) : activeTab === 'analytics' ? (
          <AnalyticsView
            analytics={analytics}
            timePeriod={timePeriod}
            onPeriodChange={setTimePeriod}
            onResetAnalytics={handleResetAnalytics}
          />
        ) : activeTab === 'subscriptions' ? (
          <SubscriptionManagement />
        ) : activeTab === 'create' ? (
          <TemplateBuilder
            editingTemplate={editingTemplate}
            onSave={(template) => {
              loadTemplates(); // Refresh the templates list
              setEditingTemplate(null); // Clear editing state
              setActiveTab('templates'); // Switch back to templates tab
            }}
            onCancel={() => {
              setEditingTemplate(null); // Clear editing state
              setActiveTab('templates');
            }}
          />
        ) : activeTab === 'audit' ? (
          <AuditDashboard user={user} />
        ) : activeTab === 'help' ? (
          <AdminHelpView />
        ) : null}
      </div>
    </div>
  );
}

/**
 * TemplatesView - List all templates with actions
 */
function TemplatesView({ templates, onTogglePublished, onEdit, onDelete }) {
  if (!templates || templates.length === 0) {
    return <div className="AdminEmpty">No templates found</div>;
  }

  return (
    <div className="AdminTemplatesList">
      <div className="AdminTemplatesHeader">
        <h2>All Templates ({templates.length})</h2>
      </div>

      <table className="AdminTable">
        <thead>
          <tr>
            <th>Title</th>
            <th>Category</th>
            <th>Pricing</th>
            <th>Deployments</th>
            <th>Purchases</th>
            <th>Published</th>
            <th>Featured</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {templates.map((t) => (
            <tr key={t.id}>
              <td>
                <div className="AdminTemplateTitle">{t.title}</div>
                <div className="AdminTemplateId">{t.id}</div>
              </td>
              <td>{t.category || '-'}</td>
              <td>
                {t.pricing_tier === 'free' ? (
                  <span className="AdminBadge AdminBadgeFree">FREE</span>
                ) : (
                  <span className="AdminBadge AdminBadgePremium">£{parseFloat(t.price_gbp || 0).toFixed(2)}</span>
                )}
              </td>
              <td>
                <strong style={{ color: t.deployment_count > 0 ? '#10b981' : '#94a3b8' }}>
                  {t.deployment_count || 0}
                </strong>
              </td>
              <td>
                <strong style={{ color: t.purchase_count > 0 ? '#8b5cf6' : '#94a3b8' }}>
                  {t.purchase_count || 0}
                </strong>
              </td>
              <td>
                <span className={`AdminBadge ${t.is_published ? 'AdminBadgeSuccess' : 'AdminBadgeDanger'}`}>
                  {t.is_published ? 'Published' : 'Draft'}
                </span>
              </td>
              <td>
                {t.featured ? '⭐' : '-'}
              </td>
              <td className="AdminTableDate">
                {t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}
              </td>
              <td>
                <div className="AdminActions">
                  <button
                    className="AdminActionBtn"
                    onClick={() => onEdit(t)}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    className="AdminActionBtn"
                    onClick={() => onTogglePublished(t.id, t.is_published)}
                    title={t.is_published ? 'Unpublish' : 'Publish'}
                  >
                    {t.is_published ? '👁️' : '🔒'}
                  </button>
                  <button
                    className="AdminActionBtn AdminActionDanger"
                    onClick={() => onDelete(t.id)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * AnalyticsView - Sales and usage statistics
 */
function AnalyticsView({ analytics, timePeriod, onPeriodChange, onResetAnalytics }) {
  if (!analytics) {
    return <div className="AdminEmpty">No analytics data available</div>;
  }

  const {
    period = 'all',
    total_revenue = 0,
    total_purchases = 0,
    total_templates = 0,
    free_templates = 0,
    premium_templates = 0,
    total_deployments = 0,
    free_deployments = 0,
    premium_deployments = 0,
    top_selling = [],
    top_deployed = [],
    recent_purchases = [],
  } = analytics;

  // Period labels for display
  const periodLabels = {
    'all': 'All Time',
    '30d': 'Last 30 Days',
    '90d': 'Last 90 Days (Quarter)',
    '365d': 'Last 365 Days (Year)',
  };

  return (
    <div className="AdminAnalytics">
      {/* Time Period Filter */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        padding: '16px 20px',
        backgroundColor: '#f8fafc',
        borderRadius: 8,
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
            Analytics Overview
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#64748b' }}>
            Showing data for: <strong>{periodLabels[period] || 'All Time'}</strong>
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label htmlFor="period-filter" style={{ fontSize: 13, fontWeight: 500, color: '#475569' }}>
            Time Period:
          </label>
          <select
            id="period-filter"
            value={timePeriod}
            onChange={(e) => onPeriodChange(e.target.value)}
            style={{
              padding: '6px 32px 6px 12px',
              fontSize: 13,
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              backgroundColor: 'white',
              cursor: 'pointer',
              outline: 'none',
              fontWeight: 500,
            }}
          >
            <option value="all">All Time</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days (Quarter)</option>
            <option value="365d">Last 365 Days (Year)</option>
          </select>
          <button
            onClick={onResetAnalytics}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              border: '1px solid #dc2626',
              borderRadius: 6,
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#dc2626';
              e.target.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#fef2f2';
              e.target.style.color = '#dc2626';
            }}
            title="Reset all analytics data (for testing)"
          >
            🗑️ Reset Analytics
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="AdminStatsGrid">
        <div className="AdminStatCard">
          <div className="AdminStatLabel">Total Revenue</div>
          <div className="AdminStatValue">£{parseFloat(total_revenue).toFixed(2)}</div>
        </div>
        <div className="AdminStatCard">
          <div className="AdminStatLabel">Total Purchases</div>
          <div className="AdminStatValue">{total_purchases}</div>
        </div>
        <div className="AdminStatCard">
          <div className="AdminStatLabel">Total Templates</div>
          <div className="AdminStatValue">{total_templates}</div>
          <div className="AdminStatMeta">
            {free_templates} free, {premium_templates} premium
          </div>
        </div>
        <div className="AdminStatCard">
          <div className="AdminStatLabel">Total Deployments</div>
          <div className="AdminStatValue">{total_deployments}</div>
          <div className="AdminStatMeta">
            {free_deployments} free, {premium_deployments} premium
          </div>
        </div>
      </div>

      {/* Top Deployed Templates */}
      {top_deployed && top_deployed.length > 0 && (
        <div className="AdminSection">
          <h2>Most Used Templates</h2>
          <table className="AdminTable">
            <thead>
              <tr>
                <th>Template</th>
                <th>Deployments</th>
                <th>Purchases</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {top_deployed.map((item, i) => (
                <tr key={i}>
                  <td>{item.title || item.template_id}</td>
                  <td><strong>{item.deployment_count}</strong></td>
                  <td>{item.purchase_count}</td>
                  <td>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 500,
                      backgroundColor: item.pricing_tier === 'free' ? '#10b981' : '#8b5cf6',
                      color: 'white'
                    }}>
                      {item.pricing_tier}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Top Selling */}
      {top_selling && top_selling.length > 0 && (
        <div className="AdminSection">
          <h2>Top Selling Templates</h2>
          <table className="AdminTable">
            <thead>
              <tr>
                <th>Template</th>
                <th>Sales</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {top_selling.map((item, i) => (
                <tr key={i}>
                  <td>{item.title || item.template_id}</td>
                  <td>{item.sales}</td>
                  <td>£{parseFloat(item.revenue || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent Purchases */}
      {recent_purchases && recent_purchases.length > 0 && (
        <div className="AdminSection">
          <h2>Recent Purchases</h2>
          <table className="AdminTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Template</th>
                <th>User</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {recent_purchases.map((p, i) => (
                <tr key={i}>
                  <td>{new Date(p.purchased_at).toLocaleString()}</td>
                  <td>{p.template_title}</td>
                  <td>{p.user_email || `User #${p.user_id}`}</td>
                  <td>£{parseFloat(p.price_paid_gbp).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * AdminHelpView - Comprehensive documentation for admin users
 */
function AdminHelpView() {
  const [helpSection, setHelpSection] = React.useState('admin');

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 0' }}>
      {/* Sub-navigation */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '2px solid #e2e8f0', paddingBottom: 12 }}>
        <button
          onClick={() => setHelpSection('admin')}
          style={{
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 600,
            border: 'none',
            borderRadius: '6px 6px 0 0',
            backgroundColor: helpSection === 'admin' ? '#3b82f6' : '#f1f5f9',
            color: helpSection === 'admin' ? 'white' : '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          📋 Admin Guide
        </button>
        <button
          onClick={() => setHelpSection('builder')}
          style={{
            padding: '8px 16px',
            fontSize: 14,
            fontWeight: 600,
            border: 'none',
            borderRadius: '6px 6px 0 0',
            backgroundColor: helpSection === 'builder' ? '#3b82f6' : '#f1f5f9',
            color: helpSection === 'builder' ? 'white' : '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          ✏️ Builder Manual
        </button>
      </div>

      {/* Content */}
      <div style={{ maxHeight: 600, overflowY: 'auto', paddingRight: 12 }}>
        {helpSection === 'admin' ? <AdminGuideContent /> : <BuilderManualContent />}
      </div>
    </div>
  );
}

/**
 * Admin Guide Content - from ADMIN_GUIDE.md
 */
function AdminGuideContent() {
  return (
    <div>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>Admin Guide</h1>
      <p style={{ color: '#64748b', marginBottom: 32 }}>Template Marketplace Administration | Last Updated: 2025-01-07</p>

      {/* Template Management */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          📋 Template Management
        </h2>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Template List Columns</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #e2e8f0' }}>Column</th>
              <th style={{ textAlign: 'left', padding: 8, borderBottom: '2px solid #e2e8f0' }}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}><strong>Title</strong></td><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>Template name and ID</td></tr>
            <tr><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}><strong>Category</strong></td><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>Template category</td></tr>
            <tr><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}><strong>Pricing</strong></td><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>FREE or Premium price in £GBP</td></tr>
            <tr><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}><strong>Deployments</strong></td><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>How many times used (all-time)</td></tr>
            <tr><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}><strong>Purchases</strong></td><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>How many times purchased</td></tr>
            <tr><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}><strong>Published</strong></td><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>Published (live) or Draft (hidden)</td></tr>
            <tr><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}><strong>Featured</strong></td><td style={{ padding: 8, borderBottom: '1px solid #e2e8f0' }}>Appears in "Featured" section</td></tr>
          </tbody>
        </table>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Template Actions</h3>
        <ul style={{ lineHeight: 1.8 }}>
          <li><strong>✏️ Edit Template:</strong> Opens Visual Builder to modify all fields</li>
          <li><strong>👁️ Publish / 🔒 Unpublish:</strong> Toggle marketplace visibility (toggle by clicking icon)</li>
          <li><strong>🗑️ Delete Template:</strong> Permanently deletes template (requires confirmation, cannot be undone)</li>
        </ul>
      </section>

      {/* Analytics */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          📊 Analytics & Reports
        </h2>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Time Period Filtering</h3>
        <p style={{ marginBottom: 12, color: '#475569', lineHeight: 1.6 }}>
          Use the dropdown to view metrics for different periods:
        </p>
        <ul style={{ lineHeight: 1.8, marginBottom: 16, color: '#475569' }}>
          <li><strong>All Time:</strong> Complete historical data</li>
          <li><strong>Last 30 Days:</strong> Monthly view</li>
          <li><strong>Last 90 Days (Quarter):</strong> Quarterly view</li>
          <li><strong>Last 365 Days (Year):</strong> Annual view</li>
        </ul>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>What Gets Filtered</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ padding: 16, backgroundColor: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
            <h4 style={{ fontSize: 16, marginBottom: 8, color: '#166534' }}>Time-Filtered Metrics</h4>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6, fontSize: 14 }}>
              <li>✅ Total Revenue</li>
              <li>✅ Total Purchases</li>
              <li>✅ Top Selling Templates</li>
              <li>✅ Recent Purchases</li>
            </ul>
          </div>
          <div style={{ padding: 16, backgroundColor: '#f1f5f9', borderRadius: 8, border: '1px solid #cbd5e1' }}>
            <h4 style={{ fontSize: 16, marginBottom: 8, color: '#475569' }}>Always Current State</h4>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6, fontSize: 14 }}>
              <li>❌ Template Counts</li>
              <li>❌ Deployment Counts</li>
            </ul>
          </div>
        </div>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>🗑️ Reset Analytics</h3>
        <div style={{ padding: 16, backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, marginTop: 12 }}>
          <p style={{ margin: 0, color: '#dc2626', fontWeight: 600, fontSize: 16 }}>⚠️ Development Tool Only</p>
          <p style={{ margin: '8px 0 0 0', fontSize: 14, color: '#991b1b', lineHeight: 1.6 }}>
            The red "Reset Analytics" button permanently deletes ALL purchase records and resets counters to 0.
            This is for testing purposes only and cannot be undone. Use with extreme caution.
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: 14, color: '#991b1b' }}>
            <strong>What it does:</strong>
          </p>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 20, fontSize: 14, color: '#991b1b' }}>
            <li>Deletes ALL records from user_template_purchases</li>
            <li>Resets deployment_count to 0 for all templates</li>
            <li>Resets purchase_count to 0 for all templates</li>
          </ul>
        </div>
      </section>

      {/* Best Practices */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          💡 Best Practices
        </h2>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Template Pricing Strategy</h3>
        <ul style={{ lineHeight: 1.8, marginBottom: 16, color: '#475569' }}>
          <li><strong>Free Templates:</strong> Basic, widely-applicable templates (10-15 recommended for growth)</li>
          <li><strong>£7.99 - £19.99:</strong> Specialized, niche templates</li>
          <li><strong>£20 - £49.99:</strong> Comprehensive workflow templates</li>
          <li><strong>£50+:</strong> Enterprise/bespoke templates with extensive customization</li>
        </ul>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Template Organization</h3>
        <ul style={{ lineHeight: 1.8, marginBottom: 16, color: '#475569' }}>
          <li><strong>Categories:</strong> Keep consistent and intuitive, use "General" for widely applicable templates</li>
          <li><strong>Featured Templates:</strong> Feature your best 3-5 templates, rotate monthly, choose high conversion rates</li>
        </ul>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Analytics Monitoring</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ padding: 16, backgroundColor: '#f8fafc', borderRadius: 8 }}>
            <h4 style={{ fontSize: 16, marginBottom: 8 }}>Weekly Review</h4>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6, fontSize: 14 }}>
              <li>Check "Most Used Templates"</li>
              <li>Review "Top Selling"</li>
              <li>Monitor deployment vs purchase ratio</li>
            </ul>
          </div>
          <div style={{ padding: 16, backgroundColor: '#f8fafc', borderRadius: 8 }}>
            <h4 style={{ fontSize: 16, marginBottom: 8 }}>Monthly Review</h4>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6, fontSize: 14 }}>
              <li>Compare 30-day vs 90-day trends</li>
              <li>Adjust pricing based on performance</li>
              <li>Rotate featured templates</li>
            </ul>
          </div>
        </div>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 8 }}>Template Quality</h3>
        <div style={{ backgroundColor: '#eff6ff', padding: 16, borderRadius: 8, border: '1px solid #bfdbfe' }}>
          <p style={{ margin: 0, marginBottom: 8, color: '#1e40af', fontWeight: 600 }}>Before Publishing:</p>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6, color: '#475569' }}>
            <li>✅ Test template deployment in a real workspace</li>
            <li>✅ Verify all variables work correctly</li>
            <li>✅ Check HTML content renders properly</li>
            <li>✅ Ensure pricing is appropriate for value</li>
            <li>✅ Proofread all content for clarity and errors</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

/**
 * Builder Manual Content - condensed from VISUAL_BUILDER_MANUAL.md
 */
function BuilderManualContent() {
  return (
    <div>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>Visual Builder Manual</h1>
      <p style={{ color: '#64748b', marginBottom: 32 }}>Template Creation Guide | Last Updated: 2025-01-07</p>

      {/* Overview */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          📝 Overview
        </h2>
        <p style={{ lineHeight: 1.6, color: '#475569', marginBottom: 12 }}>
          The Visual Builder is a form-based interface for creating interactive task templates without writing code.
        </p>
        <div style={{ backgroundColor: '#f8fafc', padding: 16, borderRadius: 8 }}>
          <strong style={{ display: 'block', marginBottom: 8 }}>Key Features:</strong>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6, color: '#475569' }}>
            <li>Form-based UI (no JSON editing required)</li>
            <li>Variable system for user customization</li>
            <li>Real-time preview</li>
            <li>Template validation</li>
          </ul>
        </div>
      </section>

      {/* Variable System */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          🔧 Variable System
        </h2>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 12 }}>Variable Properties</h3>
        <div style={{ backgroundColor: '#f1f5f9', padding: 16, borderRadius: 8, marginBottom: 16, fontFamily: 'monospace', fontSize: 13 }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`{
  "id": "unique_identifier",
  "type": "text|number|date|...",
  "label": "Field Label",
  "description": "Helper text",
  "required": true|false,
  "default": "Default value"
}`}</pre>
        </div>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 12 }}>6 Variable Types</h3>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>1. Text Input</h4>
          <p style={{ margin: 0, marginBottom: 8, color: '#475569', lineHeight: 1.6 }}>
            <strong>Use For:</strong> Names, descriptions, free text
          </p>
          <div style={{ backgroundColor: '#f8fafc', padding: 12, borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }}>
            <pre style={{ margin: 0 }}>{`{ "id": "project_name", "type": "text", "label": "Project Name", "required": true }`}</pre>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>2. Number Input</h4>
          <p style={{ margin: 0, marginBottom: 8, color: '#475569', lineHeight: 1.6 }}>
            <strong>Use For:</strong> Quantities, sizes, counts, budgets
          </p>
          <div style={{ backgroundColor: '#f8fafc', padding: 12, borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }}>
            <pre style={{ margin: 0 }}>{`{ "id": "team_size", "type": "number", "label": "Team Size", "min": 1, "max": 100 }`}</pre>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>3. Date Input</h4>
          <p style={{ margin: 0, marginBottom: 8, color: '#475569', lineHeight: 1.6 }}>
            <strong>Use For:</strong> Deadlines, start dates, milestones
          </p>
          <div style={{ backgroundColor: '#f8fafc', padding: 12, borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }}>
            <pre style={{ margin: 0 }}>{`{ "id": "launch_date", "type": "date", "label": "Launch Date", "required": true }`}</pre>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>4. Dropdown (Select)</h4>
          <p style={{ margin: 0, marginBottom: 8, color: '#475569', lineHeight: 1.6 }}>
            <strong>Use For:</strong> Choosing one option from a list
          </p>
          <div style={{ backgroundColor: '#f8fafc', padding: 12, borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }}>
            <pre style={{ margin: 0 }}>{`{ "id": "priority", "type": "dropdown", "options": ["High", "Medium", "Low"] }`}</pre>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>5. Checkbox (Boolean)</h4>
          <p style={{ margin: 0, marginBottom: 8, color: '#475569', lineHeight: 1.6 }}>
            <strong>Use For:</strong> Yes/No toggles, feature flags
          </p>
          <div style={{ backgroundColor: '#f8fafc', padding: 12, borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }}>
            <pre style={{ margin: 0 }}>{`{ "id": "include_testing", "type": "checkbox", "label": "Include Testing?" }`}</pre>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 16, marginBottom: 8, color: '#1e293b' }}>6. Checkbox Group (Multi-select)</h4>
          <p style={{ margin: 0, marginBottom: 8, color: '#475569', lineHeight: 1.6 }}>
            <strong>Use For:</strong> Selecting multiple options from a list
          </p>
          <div style={{ backgroundColor: '#f8fafc', padding: 12, borderRadius: 6, fontSize: 13, fontFamily: 'monospace' }}>
            <pre style={{ margin: 0 }}>{`{ "id": "platforms", "type": "checkbox_group", "options": ["iOS", "Android", "Web"] }`}</pre>
          </div>
        </div>
      </section>

      {/* Variable Substitution */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          🔀 Variable Substitution
        </h2>

        <div style={{ backgroundColor: '#eff6ff', padding: 16, borderRadius: 8, marginBottom: 16, border: '1px solid #bfdbfe' }}>
          <p style={{ margin: 0, marginBottom: 8, color: '#1e40af', fontWeight: 600 }}>Substitution Syntax:</p>
          <p style={{ margin: 0, fontFamily: 'monospace', fontSize: 14, color: '#475569' }}>
            {"{{variable_id}}"}
          </p>
        </div>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 12 }}>Example</h3>
        <div style={{ backgroundColor: '#f8fafc', padding: 16, borderRadius: 8 }}>
          <p style={{ margin: 0, marginBottom: 12, fontSize: 14 }}>
            <strong>Variable:</strong><br/>
            <code style={{ fontSize: 13 }}>{'{ "id": "client_name", "type": "text", "label": "Client Name" }'}</code>
          </p>
          <p style={{ margin: 0, marginBottom: 12, fontSize: 14 }}>
            <strong>Template Task:</strong><br/>
            <code style={{ fontSize: 13 }}>{'{ "title": "Send proposal to {{client_name}}" }'}</code>
          </p>
          <p style={{ margin: 0, marginBottom: 12, fontSize: 14 }}>
            <strong>User Input:</strong> "Acme Corp"
          </p>
          <p style={{ margin: 0, fontSize: 14 }}>
            <strong>Result:</strong><br/>
            <code style={{ fontSize: 13 }}>{'{ "title": "Send proposal to Acme Corp" }'}</code>
          </p>
        </div>
      </section>

      {/* Task Configuration */}
      <section style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 22, marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
          ✅ Task Configuration
        </h2>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 12 }}>Task Properties</h3>
        <div style={{ backgroundColor: '#f1f5f9', padding: 16, borderRadius: 8, fontFamily: 'monospace', fontSize: 13 }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{`{
  "title": "Task title with {{variables}}",
  "description": "Optional description with {{variables}}",
  "status": "todo|doing|done"
}`}</pre>
        </div>

        <h3 style={{ fontSize: 18, marginTop: 20, marginBottom: 12 }}>Best Practices</h3>
        <div style={{ backgroundColor: '#fef3c7', padding: 16, borderRadius: 8, border: '1px solid #fde047' }}>
          <p style={{ margin: 0, marginBottom: 8, fontSize: 14, fontWeight: 600, color: '#854d0e' }}>Task Titles:</p>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6, fontSize: 14, color: '#475569' }}>
            <li>Keep concise (under 60 characters)</li>
            <li>Use action verbs (Create, Review, Deploy)</li>
            <li>Include key variables for context</li>
            <li>Make titles scannable</li>
          </ul>
          <p style={{ margin: '12px 0 8px 0', fontSize: 14, fontWeight: 600, color: '#854d0e' }}>Task Descriptions:</p>
          <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.6, fontSize: 14, color: '#475569' }}>
            <li>Provide context and details</li>
            <li>Include markdown checklists for subtasks</li>
            <li>Link to resources (URLs, docs)</li>
            <li>Use formatting for readability</li>
          </ul>
        </div>
      </section>

      {/* Footer */}
      <div style={{ padding: 16, backgroundColor: '#eff6ff', border: '2px solid #3b82f6', borderRadius: 8 }}>
        <p style={{ margin: 0, fontSize: 14, color: '#1e40af', textAlign: 'center' }}>
          <strong>For complete examples and advanced techniques,</strong> see <code>/docs/VISUAL_BUILDER_MANUAL.md</code> (860 lines)
        </p>
      </div>
    </div>
  );
}
